import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';

import {lockBodyScrollFreeze, unlockBodyScrollFreezeAndRestore} from '@amblique/king-living/app/utils/utils';

/** Chakra Drawer default motion — fallback when onCloseComplete does not fire (iOS Simulator). */
const DRAWER_SCROLL_RELEASE_FALLBACK_MS = 400;

/**
 * Lock window scroll while a mobile PDP drawer is open and restore on close.
 * Uses onCloseComplete when available; schedules a one-shot fallback release so
 * Simulator and real devices stay in sync.
 *
 * @param {boolean} isOpen
 * @param {boolean} isMobile - Must be strict `true` (not breakpoint `undefined` during SSR)
 */
export function useMobilePdpDrawerScrollLock(isOpen, isMobile) {
  const savedScrollYRef = useRef(0);
  const cancelRestoreScrollRef = useRef(null);
  const didReleaseScrollRef = useRef(false);

  const releaseScrollLock = useCallback(() => {
    if (isMobile !== true || typeof window === 'undefined') return;
    if (didReleaseScrollRef.current) return;

    didReleaseScrollRef.current = true;
    cancelRestoreScrollRef.current?.();
    cancelRestoreScrollRef.current = unlockBodyScrollFreezeAndRestore(savedScrollYRef.current);

    window.dispatchEvent(new Event('scroll'));
  }, [isMobile]);

  useLayoutEffect(() => {
    if (isMobile !== true || !isOpen || typeof document === 'undefined') return undefined;

    didReleaseScrollRef.current = false;
    savedScrollYRef.current = window.scrollY ?? document.documentElement.scrollTop ?? 0;
    lockBodyScrollFreeze(savedScrollYRef.current);
  }, [isMobile, isOpen]);

  useEffect(() => () => cancelRestoreScrollRef.current?.(), []);

  useEffect(() => {
    if (isMobile !== true || isOpen) return undefined;

    const timeoutId = window.setTimeout(releaseScrollLock, DRAWER_SCROLL_RELEASE_FALLBACK_MS);

    return () => clearTimeout(timeoutId);
  }, [isOpen, isMobile, releaseScrollLock]);

  const handleDrawerCloseComplete = useCallback(() => {
    releaseScrollLock();
  }, [releaseScrollLock]);

  return {handleDrawerCloseComplete};
}
